/**
 * T-18 — decision-list slice primitives (DS-1g-06 §6) — AC-1g-19 (§6.2:
 * "slice primitives, zero seam change").
 *
 * `Decision` mirrors Skill #3's collapsed `view` output row 1:1
 * (goalkeeper/src/skills/decision_log_dayone/types.rs `Decision` +
 * `DecisionCategory`, flattened per its own `#[serde(flatten)]`) — field
 * names are kept snake_case to match the wire shape verbatim (this is a
 * consumed read model, never round-tripped back through a different
 * shape). Carrier rows never appear here — the fold (1f's `view::view`)
 * emits per canonical only (design §4.5 Lock 1); this type has no field
 * that could even carry a carrier marker.
 *
 * `ViewFilter` mirrors the LOCKED 1f `view` request seam exactly —
 * `include_resolved` + `criticality_tier`, nothing else. TW-1g-CONTRACT-
 * DRIFT: this seam must never widen from the PWA side; `sliceViewFilter`
 * below is written so that constructing a `ViewFilter` with any extra
 * property is a TypeScript error (no index signature, no `unknown`
 * escape hatch).
 */

export type DecisionStatus = "open" | "resolved";

export interface Decision {
  id: string;
  author?: string;
  author_name?: string;
  decision_id: string;
  text: string;
  // --- DecisionCategory, flattened (all optional, human-set, never inferred) ---
  subject?: string;
  phase?: string;
  room_zone?: string;
  trade?: string;
  system?: string;
  item_type?: string;
  decides?: string;
  input?: string;
  owner?: string;
  assignee?: string;
  owner_needed?: boolean;
  criticality_tier?: string;
  // --- decision fields ---
  status: DecisionStatus;
  first_raised_ts: string; // ISO
  resolved_ts?: string; // ISO
  rationale?: string;
  source_refs: string[];
  template_version?: string;
}

/** The LOCKED 1f `view` request seam — exactly these two fields, no more
 * (TW-1g-CONTRACT-DRIFT). `include_resolved` defaults false server-side
 * (open-only walk-of-shame); `criticality_tier` narrows only, never
 * re-orders (AC-1f-13 / AC-1g-19). */
export interface ViewFilter {
  include_resolved: boolean;
  criticality_tier?: string;
}

export type AgeBucket = "today" | "this_week" | "older";

export function ageDays(decision: Decision, now: Date = new Date()): number {
  const raised = new Date(decision.first_raised_ts).getTime();
  return Math.floor((now.getTime() - raised) / (24 * 60 * 60 * 1000));
}

/** Presentation age-severity band (design screen 5 colour-coding): a pure
 * derivation of `ageDays`, NOT a new read axis — critical ≥30d (red),
 * warn ≥7d (amber), else calm (muted). Ordering is untouched; this only
 * tints an already-ordered row. */
export type AgeSeverity = "calm" | "warn" | "critical";
export function ageSeverity(decision: Decision, now: Date = new Date()): AgeSeverity {
  const days = ageDays(decision, now);
  if (days >= 30) return "critical";
  if (days >= 7) return "warn";
  return "calm";
}

function ageBucket(decision: Decision, now: Date): AgeBucket {
  const days = ageDays(decision, now);
  if (days < 1) return "today";
  if (days < 7) return "this_week";
  return "older";
}

/** Oldest-first is the PRIMARY and ONLY ordering (AC-1g-09/19) — every
 * slice primitive below preserves whatever order it's handed and never
 * itself re-sorts; `oldestFirst` is the one function that establishes
 * order, applied once at the top of a slice pipeline. */
export function oldestFirst(decisions: Decision[]): Decision[] {
  return [...decisions].sort(
    (a, b) => new Date(a.first_raised_ts).getTime() - new Date(b.first_raised_ts).getTime(),
  );
}

export function byStatus(decisions: Decision[], status: DecisionStatus): Decision[] {
  return decisions.filter((d) => d.status === status);
}

export function byAgeBucket(decisions: Decision[], bucket: AgeBucket, now: Date = new Date()): Decision[] {
  return decisions.filter((d) => ageBucket(d, now) === bucket);
}

export function byPhase(decisions: Decision[], phase: string): Decision[] {
  return decisions.filter((d) => d.phase === phase);
}

export function bySubject(decisions: Decision[], subject: string): Decision[] {
  return decisions.filter((d) => d.subject === subject);
}

export function limit(decisions: Decision[], n: number): Decision[] {
  return decisions.slice(0, n);
}

/** Narrows a `Decision[]` by the two seam-locked filters ONLY — the pass-
 * through mirror of `ViewFilter`, used when the collapsed read already
 * returned server-filtered rows and the caller wants a client-side
 * re-application (e.g. against a locally-cached read). No other axis is
 * accepted (grep target: no `ViewFilter`-shaped object anywhere in this
 * module or its consumers carries a third field). */
export function applyViewFilter(decisions: Decision[], filter: ViewFilter): Decision[] {
  let result = filter.include_resolved ? decisions : byStatus(decisions, "open");
  if (filter.criticality_tier !== undefined) {
    result = result.filter((d) => d.criticality_tier === filter.criticality_tier);
  }
  return result;
}

export interface SliceConfig {
  filters: ViewFilter;
  limit?: number;
}

/** Stage-A neutral default (§6.3): open-only, oldest-first, no limit.
 * Stage B sets the VALUES here — and ONLY here — per the §0 allowlist
 * delta; this file's function bodies are not touched by that edit. */
export const DEFAULT_SLICE: SliceConfig = {
  filters: { include_resolved: false },
  limit: undefined,
};

/** Interprets a `SliceConfig` against a `Decision[]` — the same primitives
 * above, composed: oldest-first order established once, then the view
 * filter narrows, then an optional count limit. */
export function applySlice(decisions: Decision[], config: SliceConfig): Decision[] {
  let result = oldestFirst(decisions);
  result = applyViewFilter(result, config.filters);
  if (config.limit !== undefined) {
    result = limit(result, config.limit);
  }
  return result;
}
